import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";

import { useToast } from "src/context/ToastContext";
import { CriteriaGroupEntity } from "src/entities/CriteriaGroupEntity";
import CriteriaGroupService, { criteriaGroupQueryKeys } from "src/services/CriteriaGroupService";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const criteriaGroupService = new CriteriaGroupService();

type GroupItem = {
	id?: number;
	title: string;
	originalTitle?: string;
};

type StepGroupsDraft = {
	groups: GroupItem[];
	removedIds: number[];
};

type StepGroupsProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

export default function StepGroups({ criteriaId, backLabel, nextLabel, onBack, onComplete }: StepGroupsProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepGroupsDraft>(criteriaId, "groups"));
	const [groups, setGroups] = useState<GroupItem[]>(() => draftRef.current?.groups ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);

	const { data: queryData, isLoading } = useQuery<CriteriaGroupEntity[]>(
		criteriaGroupQueryKeys.groups(String(criteriaId)),
		() => criteriaGroupService.getGroups(criteriaId).then(response => response.data.groups),
		{ refetchOnWindowFocus: false }
	);

	useEffect(() => {
		if (!draftRef.current && queryData) {
			setGroups(queryData.map(group => ({ 
				id: group.id, 
				title: group.title, 
				originalTitle: group.title 
			})));
		};
	}, [queryData]);

	const mutation = useMutation(
		async () => {
			const created = groups.filter(group => !group.id && group.title.trim());
			const updated = groups.filter(group => group.id && group.title !== group.originalTitle);
			const calls: Promise<unknown>[] = [];

			if (created.length > 0) {
				calls.push(criteriaGroupService.postGroups({ criteriaId, groups: created.map(group => ({ title: group.title })) }));
			}

			if (updated.length > 0) {
				calls.push(criteriaGroupService.putGroups({ groups: updated.map(group => ({ id: group.id!, title: group.title })) }));
			}

			if (removedIds.length > 0) {
				calls.push(criteriaGroupService.deleteGroups({ criteriaId, groups: removedIds }));
			}

			await Promise.all(calls);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "groups");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleAddGroup() {
		setGroups(prev => [...prev, { title: "" }]);
	}

	function handleTitleChange(index: number, value: string) {
		setGroups(prev => prev.map((group, idx) => idx === index ? { ...group, title: value } : group));
	}

	function handleRemove(index: number) {
		const group = groups[index];
		
		if (group.id) {
			setRemovedIds(prev => [...prev, group.id!]);
		}

		setGroups(prev => prev.filter((_, idx) => idx !== index));
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "groups", { groups, removedIds });
		onBack();
	}

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Grupos
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<Row key={skeletonIndex} className="mb-3 align-items-end">
								<Col>
									<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
									<div className="skeleton skeleton-button" style={{ height: 38 }} />
								</Col>
								<Col xs="auto" className="d-flex align-items-end">
									<div className="skeleton skeleton-button" style={{ height: 38, width: 44 }} />
								</Col>
							</Row>
						))
					) : groups.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						groups.map((group, index) => (
							<Row key={index} className="mb-3 align-items-end">
								<Col>
									<Form.Label className="text-muted"><small>Título *</small></Form.Label>
									<Form.Control
										type="text"
										placeholder="Informe aqui"
										required
										value={group.title}
										onChange={(e) => handleTitleChange(index, e.target.value)}
									/>
								</Col>
								<Col xs="auto" className="d-flex align-items-end">
									<Button variant="light" className="rounded-3 shadow-sm" type="button" onClick={() => handleRemove(index)}>
										<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
									</Button>
								</Col>
							</Row>
						))
					)}
				</Card.Body>
				<Card.Footer className="bg-white">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={handleAddGroup}
						disabled={isLoading}
					>
						<Icon path={mdiPlus} size={1} /> Adicionar Grupo
					</Button>
				</Card.Footer>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={() => mutation.mutate()}
			/>
		</>
	);
}
